/* BMSM product-card quantity enhancer (variant-based) - UPDATED
   - Uses `tw:hidden` instead of inline style display toggles
   - Scopes all DOM queries to `.product-card`
   - On load: shows loading on ADD, fetches /cart.js, then swaps cards already in cart to qty controls
   - After ADD (AJAX or native form): flips to [- qty +]
   - Updates use /cart/change.js with line item `key` (bundle lines with `_bundle_id` excluded)
   - VIEW button is hidden whenever quantity > 0
   - Fallback: if /cart.js fails, buttons remain default
   - DEBOUNCE: 1s delay before cart update, allows rapid quantity changes
   - ENHANCED: Listens to multiple cart events and polls for external changes
   - FIX: Re-enables qty buttons after item re-added (no more permanently disabled state)
*/

(function () {
  const GREEN = "#2D793A";
  const DEBOUNCE_DELAY = 1000; // 1 second delay
  const CART_POLL_INTERVAL = 2000; // Poll every 2 seconds for external changes
  const pendingUpdates = new Map(); // Track pending updates per variant
  let lastCartState = null;
  let pollInterval = null;

  /* Bundle lines use `properties._bundle_id`. Those are ignored for BMSM card qty + change
     targets. Lines added via this script's "up from zero" include `_bundle_id` (required) plus
     `_bmsm_card` so they remain visible to the card UI. CYG bundle lines have `_bundle_id` only. */
  function itemHasBundleIdProperty(item) {
    const p = item.properties;
    if (!p) return false;
    if (Array.isArray(p)) {
      return p.some((x) => x.name === "_bundle_id" && x.value != null && String(x.value) !== "");
    }
    if (typeof p === "object") {
      return p._bundle_id != null && String(p._bundle_id) !== "";
    }
    return false;
  }

  function itemIsBmsmCardLine(item) {
    const p = item.properties;
    if (!p) return false;
    if (Array.isArray(p)) {
      return p.some((x) => x.name === "_bmsm_card" && (x.value === "1" || x.value === true));
    }
    if (typeof p === "object") {
      return p._bmsm_card === "1" || p._bmsm_card === true;
    }
    return false;
  }

  /** Lines that count toward BMSM card qty and may be updated via +/- (not CYG-only bundle rows). */
  function itemIgnoredForBmsmCard(item) {
    if (!itemHasBundleIdProperty(item)) return false;
    return !itemIsBmsmCardLine(item);
  }

  function getManagedLinesForVariant(cart, variantId) {
    if (!cart?.items?.length) return [];
    return cart.items.filter((i) => i.variant_id === variantId && !itemIgnoredForBmsmCard(i));
  }

  function getBmsmDisplayQuantity(cart, variantId) {
    return getManagedLinesForVariant(cart, variantId).reduce((sum, i) => sum + (i.quantity || 0), 0);
  }

  document.addEventListener("DOMContentLoaded", init);

  function init() {
    const cards = [...document.querySelectorAll(".product-card")];
    if (!cards.length) return;

    cards.forEach((card) => setAtcLoading(card, true));

    attachAddInterceptors(cards);
    setupCartEventListeners();
    startCartPolling(); // Start polling for external changes

    fetchCart()
      .then((cart) => {
        lastCartState = cart;
        cards.forEach((card) => {
          const variantId = getVariantIdFromCard(card);
          if (!variantId) return;
          const qty = getBmsmDisplayQuantity(cart, variantId);
          if (qty > 0) {
            renderQty(card, variantId, qty);
          }
        });
      })
      .catch(() => {
        console.warn("[BMSM] Cart fetch failed; leaving default buttons.");
      })
      .finally(() => {
        cards.forEach((card) => setAtcLoading(card, false));
      });
  }

  /* ---------------- Cart Polling for External Changes ---------------- */

  function startCartPolling() {
    // Poll the cart periodically to catch external changes
    pollInterval = setInterval(async () => {
      try {
        const cart = await fetchCart();
        checkAndUpdateFromCart(cart);
      } catch (e) {
        console.warn("[BMSM] Cart poll failed", e);
      }
    }, CART_POLL_INTERVAL);
  }

  function checkAndUpdateFromCart(cart) {
    if (!cart || !cart.items) return;

    // Update all cards
    document.querySelectorAll(".product-card").forEach((card) => {
      const variantId = getVariantIdFromCard(card);
      if (!variantId) return;

      // Skip if there's a pending update for this variant (user is actively changing it)
      if (pendingUpdates.has(variantId)) {
        // But if the pending update is for quantity 0, don't override it
        const pending = pendingUpdates.get(variantId);
        if (pending.targetQuantity !== 0) {
          return;
        }
      }

      const newQty = getBmsmDisplayQuantity(cart, variantId);
      const el = qInCard(card, ".product-card-qty");
      const currentDisplayQty = el ? parseInt(el.querySelector(".product-card-qty-num")?.textContent, 10) : 0;

      // Only update if quantity actually changed
      if (newQty !== currentDisplayQty) {
        if (newQty > 0) {
          renderQty(card, variantId, newQty);
          hideView(card);
        } else {
          showAddHideQty(card);
          showView(card);
        }
      }
    });

    lastCartState = cart;
  }

  /* ---------------- Setup Cart Event Listeners ---------------- */

  function setupCartEventListeners() {
    // Listen to our own custom event
    const handleCartUpdate = (e) => {
      const { variantId, quantity, cart } = e.detail || {};

      if (cart) {
        checkAndUpdateFromCart(cart);
      } else if (variantId !== undefined) {
        // Single variant update
        document.querySelectorAll(".product-card").forEach((card) => {
          const v = getVariantIdFromCard(card);
          if (v !== variantId) return;

          if (pendingUpdates.has(variantId)) return;

          if (quantity > 0) {
            renderQty(card, variantId, quantity);
            hideView(card);
          } else {
            showAddHideQty(card);
            showView(card);
          }
        });
      }
    };

    // Listen on both document and window
    document.addEventListener("updated.ecomify.cart", handleCartUpdate);
    window.addEventListener("updated.ecomify.cart", handleCartUpdate);

    // Listen to common Shopify cart events
    document.addEventListener("cart:updated", handleCartUpdate);
    window.addEventListener("cart:updated", handleCartUpdate);

    document.addEventListener("cart.changed", handleCartUpdate);
    window.addEventListener("cart.changed", handleCartUpdate);

    // Shopify Ajax API cart update event
    document.addEventListener("ajaxCart.afterCartLoad", handleCartUpdate);
    window.addEventListener("ajaxCart.afterCartLoad", handleCartUpdate);

    // Theme-specific events (common patterns)
    document.addEventListener("theme:cart:change", handleCartUpdate);
    window.addEventListener("theme:cart:change", handleCartUpdate);

    // Fetch and update on any cart-related event
    const refreshFromCartEvents = ["cart:refresh", "cart.requestComplete"];
    refreshFromCartEvents.forEach((eventName) => {
      const handler = async () => {
        try {
          const cart = await fetchCart();
          checkAndUpdateFromCart(cart);
        } catch (e) {
          console.warn("[BMSM] Failed to refresh cart on event", e);
        }
      };
      document.addEventListener(eventName, handler);
      window.addEventListener(eventName, handler);
    });
  }

  /* ---------------- DOM helpers ---------------- */

  function qInCard(card, sel) {
    return card.querySelector(sel);
  }

  function getVariantIdFromCard(card) {
    const input = qInCard(card, 'form.atc-form input[name="id"]');
    if (!input) return null;
    const v = parseInt(input.value, 10);
    return Number.isFinite(v) ? v : null;
  }

  function setAtcLoading(card, isLoading) {
    const btn = qInCard(card, "form.atc-form .atc-btn");
    if (!btn) return;
    btn.classList.toggle("is-loading", !!isLoading);
    if (isLoading) {
      btn.setAttribute("disabled", "");
      btn.setAttribute("aria-busy", "true");
    } else {
      btn.removeAttribute("disabled");
      btn.removeAttribute("aria-busy");
    }
  }

  function hideAddShowQty(card) {
    const form = qInCard(card, "form.atc-form");
    if (form) form.classList.add("tw:hidden");
    const qty = qInCard(card, ".product-card-qty");
    if (qty) qty.classList.remove("tw:hidden");
  }

  function showAddHideQty(card) {
    const form = qInCard(card, "form.atc-form");
    if (form) form.classList.remove("tw:hidden");
    const qty = qInCard(card, ".product-card-qty");
    if (qty) qty.classList.add("tw:hidden");
    showView(card);
  }

  function hideView(card) {
    const view = qInCard(card, ".product-card-view-container");
    if (view) view.classList.add("tw:hidden");
  }

  function showView(card) {
    const view = qInCard(card, ".product-card-view-container");
    if (view) view.classList.remove("tw:hidden");
  }

  function ensureQtyContainer(card) {
    let el = qInCard(card, ".product-card-qty");
    if (el) return el;

    // If your markup has been converted to tw:, these selectors also use tw: (colons escaped)
    const actionsRow = qInCard(card, ".tw\\:mt-auto");
    if (!actionsRow) {
      // Fallback: try to find the grid container directly
      const gridContainer = qInCard(card, ".tw\\:grid.tw\\:grid-cols-2");
      if (!gridContainer) return null;

      el = document.createElement("div");
      el.className = "product-card-qty tw:relative tw:z-30 tw:flex-1 tw:basis-0 tw:min-w-0 tw:col-span-2 tw:hidden";
      el.innerHTML = `
          <div class="tw:flex tw:items-center tw:justify-between tw:px-[12px] tw:py-[7px] tw:text-[18px] tw:leading-none tw:tracking-[0.18em] tw:uppercase tw:font-afacad tw:w-full"
               style="background:${GREEN}; color:white;">
            <button type="button" class="product-card-qty-btn tw:shrink-0 tw:px-3 tw:py-1" data-act="dec" aria-label="Decrease">−</button>
            <div class="product-card-qty-num tw:select-none" aria-live="polite" aria-atomic="true">1</div>
            <button type="button" class="product-card-qty-btn tw:shrink-0 tw:px-3 tw:py-1" data-act="inc" aria-label="Increase">+</button>
          </div>
        `;

      const view = qInCard(card, ".product-card-view-container");
      gridContainer.insertBefore(el, view || null);
      return el;
    }

    el = document.createElement("div");
    el.className = "product-card-qty tw:relative tw:z-30 tw:flex-1 tw:basis-0 tw:min-w-0 tw:col-span-2 tw:hidden";
    el.innerHTML = `
        <div class="tw:flex tw:items-center tw:justify-between tw:px-[12px] tw:py-[9px] tw:text-[18px] tw:leading-none tw:tracking-[0.18em] tw:uppercase tw:font-afacad tw:w-full"
             style="background:${GREEN}; color:white;">
          <button type="button" class="product-card-qty-btn tw:shrink-0 tw:px-3 tw:py-1" data-act="dec" aria-label="Decrease">−</button>
          <div class="product-card-qty-num tw:select-none" aria-live="polite" aria-atomic="true">1</div>
          <button type="button" class="product-card-qty-btn tw:shrink-0 tw:px-3 tw:py-1" data-act="inc" aria-label="Increase">+</button>
        </div>
      `;

    const view = qInCard(card, ".product-card-view-container");
    actionsRow.insertBefore(el, view || null);
    return el;
  }

  /* ---------------- Debounced Updates ---------------- */

  function debouncedUpdateCart(card, variantId, quantity) {
    const el = qInCard(card, ".product-card-qty");
    if (!el) return;

    const num = el.querySelector(".product-card-qty-num");

    // Clear existing timeout for this variant
    if (pendingUpdates.has(variantId)) {
      clearTimeout(pendingUpdates.get(variantId).timeout);
    }

    // Immediately update the display to show user input
    num.textContent = String(quantity);

    // Set up the debounced fetch
    const timeoutId = setTimeout(async () => {
      const targetQty = quantity;

      // For zero quantity, hide VIEW button before showing spinner
      if (targetQty === 0) {
        hideView(card);
      }

      // Replace number with spinner
      num.innerHTML =
        '<div class="tw:inline-block tw:w-4 tw:h-4 tw:border-2 tw:border-white tw:border-t-transparent tw:rounded-full tw:animate-spin"></div>';

      // Disable buttons during the fetch
      el.querySelectorAll(".product-card-qty-btn").forEach((btn) => (btn.disabled = true));

      try {
        // Perform the cart update
        const ok = await applyVariantQuantityTarget(variantId, targetQty);

        if (!ok) {
          // Failed - fetch actual cart state and revert
          try {
            const cart = await fetchCart();
            const actualQty = getBmsmDisplayQuantity(cart, variantId);

            if (actualQty === 0) {
              // Should show ADD button
              showAddHideQty(card);
              showView(card);
            } else {
              // Restore the actual quantity
              num.textContent = String(actualQty);
              el.querySelectorAll(".product-card-qty-btn").forEach((btn) => (btn.disabled = false));
              hideView(card);
            }
          } catch (e) {
            console.error("[BMSM] Failed to fetch cart after error", e);
            // Last resort: restore what we tried to set
            num.textContent = String(targetQty);
            el.querySelectorAll(".product-card-qty-btn").forEach((btn) => (btn.disabled = false));
          }
          return;
        }

        // Success! Now update UI based on final quantity
        if (targetQty === 0) {
          // Transition to ADD button
          showAddHideQty(card);
          showView(card);
        } else {
          // Show the confirmed quantity and re-enable buttons
          num.textContent = String(targetQty);
          el.querySelectorAll(".product-card-qty-btn").forEach((btn) => (btn.disabled = false));
          hideView(card);
        }
      } catch (e) {
        console.error("[BMSM] Unexpected error during cart update", e);
        // Restore display and re-enable
        num.textContent = String(targetQty);
        el.querySelectorAll(".product-card-qty-btn").forEach((btn) => (btn.disabled = false));
      } finally {
        // Clear the pending update
        pendingUpdates.delete(variantId);
      }
    }, DEBOUNCE_DELAY);

    // Store the pending update
    pendingUpdates.set(variantId, {
      timeout: timeoutId,
      targetQuantity: quantity,
    });
  }

  /* ---------------- Rendering & Events ---------------- */

  function renderQty(card, variantId, quantity) {
    const el = ensureQtyContainer(card);
    if (!el) {
      console.error("[BMSM] Could not create/find quantity container");
      return;
    }

    const num = el.querySelector(".product-card-qty-num");
    num.textContent = String(quantity);

    el.dataset.confirmedQty = String(quantity);

    // IMPORTANT FIX: Always (re)enable buttons and (re)bind handlers on render
    const btns = el.querySelectorAll(".product-card-qty-btn");
    btns.forEach((btn) => {
      btn.disabled = false; // FIX: ensure not stuck disabled after re-add
      btn.onclick = () => {
        const act = btn.dataset.act;
        const current = parseInt(num.textContent, 10) || 0;
        const next = act === "inc" ? current + 1 : Math.max(0, current - 1);
        debouncedUpdateCart(card, variantId, next);
      };
    });

    hideAddShowQty(card);

    if (quantity > 0) {
      hideView(card);
    }
  }

  /* ---------------- Network ---------------- */

  async function fetchCart() {
    const res = await fetch("/cart.js", { credentials: "same-origin" });
    if (!res.ok) throw new Error("cart.js failed");
    return res.json();
  }

  function dispatchCartUpdated(variantId, quantity, cart) {
    if (typeof window.upcartRefreshCart === "function") {
      window.upcartRefreshCart();
    }
    const eventDetail = { detail: { variantId, quantity, cart }, bubbles: true, cancelable: true };
    document.dispatchEvent(new CustomEvent("updated.ecomify.cart", eventDetail));
    window.dispatchEvent(new CustomEvent("updated.ecomify.cart", eventDetail));
  }

  async function postChangeJson(res) {
    if (!res.ok) throw new Error("cart request failed");
    return res.json();
  }

  /**
   * Sets the BMSM-managed quantity for a variant (ignores CYG `_bundle_id`-only lines).
   * Uses line `key` for /cart/change.js. If qty > 0 and no managed line exists, /cart/add.js
   * with `_bundle_id` + `_bmsm_card` (see file header).
   */
  async function applyVariantQuantityTarget(variantId, targetQty) {
    try {
      let cart = await fetchCart();

      if (targetQty === 0) {
        let managed = getManagedLinesForVariant(cart, variantId);
        for (const line of managed) {
          cart = await postChangeJson(
            await fetch("/cart/change.js", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              credentials: "same-origin",
              body: JSON.stringify({ id: line.key, quantity: 0 }),
            }),
          );
        }
        dispatchCartUpdated(variantId, 0, cart);
        return true;
      }

      // targetQty > 0
      let managed = getManagedLinesForVariant(cart, variantId);

      if (managed.length === 0) {
        const bundleId = crypto.randomUUID();
        await postChangeJson(
          await fetch("/cart/add.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "same-origin",
            body: JSON.stringify({
              id: variantId,
              quantity: targetQty,
              properties: {
                _bundle_id: bundleId,
                _bmsm_card: "1",
              },
            }),
          }),
        );
        cart = await fetchCart();
        dispatchCartUpdated(variantId, getBmsmDisplayQuantity(cart, variantId), cart);
        return true;
      }

      // Reconcile to targetQty: prefer adjusting the first managed line; if impossible because
      // other managed lines exceed target, remove qty from tail lines first (rare duplicate lines).
      for (let guard = 0; guard < 20; guard++) {
        const sum = getBmsmDisplayQuantity(cart, variantId);
        if (sum === targetQty) break;

        managed = getManagedLinesForVariant(cart, variantId);
        if (managed.length === 0) {
          const bundleId = crypto.randomUUID();
          await postChangeJson(
            await fetch("/cart/add.js", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              credentials: "same-origin",
              body: JSON.stringify({
                id: variantId,
                quantity: targetQty,
                properties: {
                  _bundle_id: bundleId,
                  _bmsm_card: "1",
                },
              }),
            }),
          );
          cart = await fetchCart();
          break;
        }

        const first = managed[0];
        const rest = managed.slice(1);
        const restSum = rest.reduce((s, l) => s + (l.quantity || 0), 0);
        const desiredFirst = targetQty - restSum;

        if (desiredFirst >= 0) {
          cart = await postChangeJson(
            await fetch("/cart/change.js", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              credentials: "same-origin",
              body: JSON.stringify({ id: first.key, quantity: desiredFirst }),
            }),
          );
          break;
        }

        const last = managed[managed.length - 1];
        cart = await postChangeJson(
          await fetch("/cart/change.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "same-origin",
            body: JSON.stringify({ id: last.key, quantity: 0 }),
          }),
        );
      }

      cart = await fetchCart();
      dispatchCartUpdated(variantId, getBmsmDisplayQuantity(cart, variantId), cart);
      return true;
    } catch (e) {
      console.error("[BMSM] Cart update failed", e);
      return false;
    }
  }

  /* ---------------- ADD interception ---------------- */

  function attachAddInterceptors(cards) {
    window.bmsmSubmitAtcForm = async function (form, card) {
      const fd = new FormData(form);
      const variantId = parseInt(fd.get("id"), 10);
      if (!Number.isFinite(variantId)) return;

      form.dataset.busy = "1";
      setAtcLoading(card, true);

      try {
        console.log("[BMSM DEBUG] Starting add to cart for variant:", variantId);

        const res = await fetch("/cart/add.js", {
          method: "POST",
          headers: { Accept: "application/json" },
          credentials: "same-origin",
          body: fd,
        });
        if (!res.ok) throw new Error("add.js failed");
        const addedItem = await res.json();
        console.log("[BMSM DEBUG] Added item response:", addedItem);

        const cartRes = await fetch("/cart.js", { credentials: "same-origin" });
        const cart = await cartRes.json();
        console.log("[BMSM DEBUG] Full cart after add:", cart);

        const actualQty = getBmsmDisplayQuantity(cart, variantId);
        console.log("[BMSM DEBUG] BMSM qty (excludes CYG-only _bundle_id lines):", actualQty);

        const qtyContainer = ensureQtyContainer(card);
        console.log("[BMSM DEBUG] Qty container created/found:", qtyContainer);

        if (actualQty > 0) {
          renderQty(card, variantId, actualQty);
          hideView(card);
        } else {
          showAddHideQty(card);
          showView(card);
        }
        console.log("[BMSM DEBUG] render after add:", { variantId, actualQty });

        const eventDetail = { detail: { variantId, quantity: actualQty, cart }, bubbles: true, cancelable: true };
        document.dispatchEvent(new CustomEvent("updated.ecomify.cart", eventDetail));
        window.dispatchEvent(new CustomEvent("updated.ecomify.cart", eventDetail));

        if (typeof window.upcartRefreshCart === "function") {
          window.upcartRefreshCart();
        }
      } catch (e) {
        console.error("[BMSM] add.js failed", e);
        console.error("[BMSM DEBUG] Full error:", e.stack);
      } finally {
        form.dataset.busy = "0";
        setAtcLoading(card, false);
      }
    };

    cards.forEach((card) => {
      const form = qInCard(card, "form.atc-form");
      if (!form) return;

      form.onsubmit = function (evt) {
        evt.preventDefault();
        if (form.dataset.busy === "1") return false;
        window.bmsmSubmitAtcForm(form, card);
        return false;
      };
    });
  }

  // Cleanup on page unload
  window.addEventListener("beforeunload", () => {
    if (pollInterval) {
      clearInterval(pollInterval);
    }
  });
})();
